import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { LogoIcon, LogoText } from '../components/Logo';

function Signup() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [newsletter, setNewsletter] = useState(true);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const { signUp, signInWithGoogle } = useAuth();
  const { t, i18n } = useTranslation(['auth', 'common']);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!agreedToTerms) {
      setError(t('auth:errorMustAgreeTerms'));
      return;
    }

    if (password !== confirmPassword) {
      setError(t('auth:errorPasswordsDontMatch'));
      return;
    }

    if (password.length < 6) {
      setError(t('auth:errorPasswordTooShort'));
      return;
    }

    setLoading(true);

    const { error } = await signUp(email, password);

    if (error) {
      setError(error.message);
      setLoading(false);
    } else {
      // Store newsletter preference - will subscribe after email verification
      if (newsletter) {
        localStorage.setItem('newsletter_pending', 'true');
      }
      setSuccess(true);
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    if (!agreedToTerms) {
      setError(t('auth:errorMustAgreeTerms'));
      return;
    }
    // Store newsletter preference before OAuth redirect
    if (newsletter) {
      localStorage.setItem('newsletter_pending', 'true');
    }
    const { error } = await signInWithGoogle();
    if (error) {
      setError(error.message);
    }
  };


  if (success) {
    return (
      <div className="min-h-screen bg-anthracite-950 flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          <div className="bg-anthracite-900 rounded-2xl p-8 border border-anthracite-700 text-center">
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-white mb-2">{t('auth:checkYourEmail')}</h2>
            <p className="text-gray-400 mb-6">
              {t('auth:confirmationSent')} <span className="text-white">{email}</span>
            </p>
            {newsletter && (
              <p className="text-sm text-gray-500 mb-6">
                {t('auth:newsletterSubscribed')}
              </p>
            )}
            <Link
              to="/login"
              className="inline-block py-3 px-6 bg-accent hover:bg-accent-dark text-white font-medium rounded-lg transition-colors"
            >
              {t('auth:backToLogin')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-anthracite-950 flex items-center justify-center px-4">
      <Helmet>
        <html lang={i18n.language} />
        <title>{t('auth:signUpTitle')}</title>
        <meta name="description" content={t('auth:signUpDesc')} />
        <link rel="canonical" href="https://scorinova.com/signup" />
      </Helmet>
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <LogoIcon className="w-10 h-10" />
            <div>
              <LogoText size="text-2xl" />
            </div>
          </div>
          <p className="text-gray-400">{t('auth:createYourFreeAccount')}</p>
        </div>

        {/* Signup Form */}
        <div className="bg-anthracite-900 rounded-2xl p-8 border border-anthracite-700">
          {/* Google Sign Up */}
          <button
            onClick={handleGoogleSignIn}
            className="w-full py-3 px-4 mb-6 bg-white hover:bg-gray-100 text-gray-800 font-medium rounded-lg transition-colors flex items-center justify-center gap-3"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {t('auth:continueWithGoogle')}
          </button>

          {/* Divider */}
          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-anthracite-700"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-anthracite-900 text-gray-500">{t('auth:orSignUpWithEmail')}</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-3 text-red-400 text-sm">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                {t('common:email')}
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-3 bg-anthracite-800 border border-anthracite-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all"
                placeholder={t('auth:emailPlaceholder')}
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
                {t('common:password')}
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-4 py-3 bg-anthracite-800 border border-anthracite-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all"
                placeholder={t('auth:atLeast6Chars')}
              />
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300 mb-2">
                {t('auth:confirmPassword')}
              </label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="w-full px-4 py-3 bg-anthracite-800 border border-anthracite-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all"
                placeholder={t('auth:confirmPasswordPlaceholder')}
              />
            </div>

            {/* Terms & Conditions */}
            <div className="bg-anthracite-800/50 rounded-xl p-4 border border-anthracite-700">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="mt-1 w-4 h-4 rounded border-anthracite-600 bg-anthracite-800 text-accent focus:ring-accent focus:ring-offset-0"
                />
                <div>
                  <span className="text-white font-medium text-sm">
                    {t('auth:agreeToTerms')}{' '}
                    <Link to="/terms" target="_blank" className="text-accent hover:text-accent-light underline">{t('common:termsAndConditions')}</Link>
                    {' '}{t('auth:and')}{' '}
                    <Link to="/privacy" target="_blank" className="text-accent hover:text-accent-light underline">{t('common:privacyPolicy')}</Link>
                  </span>
                  <p className="text-gray-500 text-xs mt-1">
                    {t('auth:termsDisclaimer')}
                  </p>
                </div>
              </label>
            </div>

            {/* Newsletter Subscription */}
            <div className="bg-anthracite-800/50 rounded-xl p-4 border border-anthracite-700">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={newsletter}
                  onChange={(e) => setNewsletter(e.target.checked)}
                  className="mt-1 w-4 h-4 rounded border-anthracite-600 bg-anthracite-800 text-accent focus:ring-accent focus:ring-offset-0"
                />
                <div>
                  <span className="text-white font-medium text-sm">{t('auth:subscribePOTD')}</span>
                  <p className="text-gray-500 text-xs mt-1">
                    {t('auth:potdDescription')}
                  </p>
                </div>
              </label>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 px-4 bg-accent hover:bg-accent-dark text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? t('auth:creatingAccount') : t('common:createAccount')}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-400">
              {t('auth:alreadyHaveAccount')}{' '}
              <Link to="/login" className="text-accent hover:text-accent-dark transition-colors">
                {t('common:signIn')}
              </Link>
            </p>
          </div>
        </div>

        <p className="mt-8 text-center text-xs text-gray-600">
          <Link to="/terms" className="hover:text-accent transition-colors">{t('common:termsAndConditions')}</Link>
          {' '}&middot;{' '}
          <Link to="/privacy" className="hover:text-accent transition-colors">{t('common:privacyPolicy')}</Link>
        </p>
      </div>
    </div>
  );
}

export default Signup;
